import { computed, type ComputedRef, nextTick, type Ref, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import type { AnyVariables, CombinedError, OperationResult } from '@urql/vue'

import type {
    LoginMutation,
    LoginWithFacebookMutation,
    LoginWithGoogleMutation,
} from '@/graphql/api'
import { useTranslator } from '@/i18n'
import { assertNever } from '@/helpers'
import { getEmail } from '@/types/email'
import { useCustomerRegistrationInputStore } from '../useCustomerRegistrationInput'
import { useIsAdmin } from '@/components/app/isAdmin'
import { useAuth } from '@/plugins/auth.ts'
import { useLoginWithGoogleMutation } from '@/graphql/api/auth/loginWithGoogle.mutation.generated.ts'
import { useLoginWithFacebookMutation } from '@/graphql/api/auth/loginWithFacebook.mutation.generated'
import { useLoginMutation } from '@/graphql/api/auth/login.mutation.generated.ts'
import { useSegment } from '@/third-party/segment.ts'

interface Login {
    identifier: Ref<string>
    password: Ref<string>
    login():
        | undefined
        | Promise<
              | LoginMutation['login']
              | LoginWithGoogleMutation['loginWithGoogle']
              | undefined
              | CombinedError
          >
    loginWithGoogle(): Promise<
        | LoginMutation['login']
        | LoginWithGoogleMutation['loginWithGoogle']
        | undefined
        | CombinedError
    >
    loginWithFacebook(): Promise<
        | LoginMutation['login']
        | LoginWithFacebookMutation['loginWithFacebook']
        | undefined
        | CombinedError
    >
    fetching: ComputedRef<boolean>
    errorMessage: ComputedRef<string>
    emailError: Ref<string>
}

export default function useLogin(): Login {
    const { t } = useI18n()

    const { setAuth } = useAuth()

    const {
        executeMutation: executeLogin,
        error: errorLogin,
        fetching: fetchingLogin,
    } = useLoginMutation()
    const {
        executeMutation: executeLoginWithGoogle,
        error: errorLoginWithGoogle,
        fetching: fetchingLoginWithGoogle,
    } = useLoginWithGoogleMutation()

    const {
        executeMutation: executeLoginWithFacebook,
        error: errorLoginWithFacebook,
        fetching: fetchingLoginWithFacebook,
    } = useLoginWithFacebookMutation()

    const fetching = computed(
        () =>
            fetchingLogin.value ||
            fetchingLoginWithGoogle.value ||
            fetchingLoginWithFacebook.value
    )

    const { locale } = useTranslator()

    const failureError = ref('')
    const errorMessage = computed(
        () =>
            errorLogin?.value?.message ||
            errorLoginWithGoogle?.value?.message ||
            errorLoginWithFacebook?.value?.message ||
            failureError.value
    )
    const emailError = ref('')

    const identifier = ref('')
    const password = ref('')

    const { login: trackLogin } = useSegment()

    const isAdmin = useIsAdmin()

    const handleLoginResponse = (
        result: OperationResult<
            LoginMutation | LoginWithGoogleMutation | LoginWithFacebookMutation,
            AnyVariables
        >
    ):
        | undefined
        | LoginMutation['login']
        | LoginWithGoogleMutation['loginWithGoogle']
        | LoginWithFacebookMutation['loginWithFacebook']
        | CombinedError => {
        const error = result.error

        if (error) {
            return error
        }

        const resultData = result.data

        if (!resultData) {
            return undefined
        }

        const data =
            'login' in resultData
                ? resultData.login
                : 'loginWithGoogle' in resultData
                  ? resultData.loginWithGoogle
                  : resultData.loginWithFacebook

        if (!data) {
            return undefined
        }

        switch (data.__typename) {
            case 'LoginResponseSuccess':
                trackLogin(data.user)

                nextTick(() => {
                    setAuth(data)
                })
                break
            case 'LoginResponseFailure':
                failureError.value = data.message
                break
            default:
                assertNever(data, data)
                break
        }

        return data
    }

    const login = ():
        | undefined
        | Promise<
              | LoginMutation['login']
              | LoginWithGoogleMutation['loginWithGoogle']
              | undefined
              | CombinedError
          > => {
        failureError.value = ''

        const email = getEmail(identifier.value)

        if (!email) {
            emailError.value = t('globals.invalid_email_error')

            return
        } else {
            emailError.value = ''
        }

        return executeLogin({
            credentials: {
                identifier: email,
                password: password.value,
            },
            locale: locale.value,
            isConsultant: isAdmin.value,
        }).then(handleLoginResponse)
    }

    const { customerRegistrationInput } = storeToRefs(
        useCustomerRegistrationInputStore()
    )

    const loginWithGoogle = (): Promise<
        | LoginMutation['login']
        | LoginWithGoogleMutation['loginWithGoogle']
        | undefined
        | CombinedError
    > => {
        if (customerRegistrationInput.value?.byGoogle) {
            return executeLoginWithGoogle({
                credentials: customerRegistrationInput.value.byGoogle,
                locale: locale.value,
                isConsultant: isAdmin.value,
            }).then(handleLoginResponse)
        }

        return Promise.reject(new Error('No Google credentials'))
    }

    const loginWithFacebook = (): Promise<
        | LoginMutation['login']
        | LoginWithFacebookMutation['loginWithFacebook']
        | undefined
        | CombinedError
    > => {
        if (customerRegistrationInput.value?.byFacebook) {
            return executeLoginWithFacebook({
                credentials: customerRegistrationInput.value.byFacebook,
                locale: locale.value,
                isConsultant: isAdmin.value,
            }).then(handleLoginResponse)
        }

        return Promise.reject(new Error('No Facebook credentials'))
    }

    return {
        identifier,
        password,
        login,
        loginWithGoogle,
        loginWithFacebook,
        fetching,
        errorMessage,
        emailError,
    }
}
